<div class="billing-page">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start align-items-sm-center mb-4 flex-wrap gap-2 page-header">
    <div class="d-flex align-items-start gap-3 min-w-0 flex-1">
      @if (isSmallSceen()) {
        <button
          mat-icon-button
          class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm d-flex align-items-center justify-content-center text-gray-500 hover:bg-gray-50 transition-colors shrink-0 mt-1"
          (click)="sidenavService.toggle()"
          [matTooltip]="(sidenavService.opened() ? 'Global.Close' : 'Global.Open') | translate"
        >
          <mat-icon class="text-[20px]! w-5! h-5!">{{ sidenavService.opened() ? 'left_panel_close' : 'left_panel_open' }}</mat-icon>
        </button>
      }
      <div class="min-w-0 flex-1">
        <h1 class="page-title">{{ 'Billing.PageTitle' | translate }}</h1>
        <p class="page-subtitle">{{ 'Billing.PageSubtitle' | translate }}</p>
      </div>
    </div>
    @if (!isSmallSceen()) {
      <button matButton (click)="openExplanationDialog()" class="text-[15px]! text-(--skz-text-muted)! p-5! rounded-[14px]! shrink-0">
        <mat-icon class="me-2">help_outline</mat-icon>
        <span>{{ 'Billing.Explanation' | translate }}</span>
      </button>
    } @else {
      <button mat-mini-fab color="basic" (click)="openExplanationDialog()" class="shrink-0 bg-white! text-gray-500! border border-gray-200 shadow-sm" [matTooltip]="'Billing.Explanation' | translate">
        <mat-icon>help_outline</mat-icon>
      </button>
    }
  </div>

  <!-- Bento Grid -->
  @if (isLoadingCredits) {
    <div class="d-flex justify-content-center w-100 py-5 mb-4">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else {
    <div class="bento-grid mb-4">
      <!-- Card 1: Credit Balance -->
      <div class="bento-card bento-card--credits p-4">
        <div class="card-header">
          <h2 class="card-label">{{ 'Billing.CreditBalance' | translate }}</h2>
          <div class="card-icon-box card-icon-box--indigo">
            <mat-icon>bolt</mat-icon>
          </div>
        </div>
        @if (creditsExhausted) {
          <div class="unlock-credits-hint mt-2">
            <p class="hint-title">{{ 'Billing.CreditsExhausted' | translate }}</p>
            <p class="hint-desc">{{ 'Billing.CreditsExhaustedDesc' | translate }}</p>
          </div>
        } @else if (hasPaymentMethod) {
          <div class="credit-counter">{{ formatNumber(animatedRemaining) }}</div>
          <p class="credit-sub">{{ 'Billing.CreditsRemaining' | translate }}</p>
        } @else {
          <div class="unlock-credits-hint mt-2">
            <p class="hint-title">{{ 'Billing.UnlockMoreCredits' | translate }}</p>
            <p class="hint-desc">{{ 'Billing.UnlockMoreCreditsDesc' | translate }}</p>
          </div>
        }

        <!-- Usage Bar -->
        <div class="usage-bar-section mt-4">
          <div class="usage-bar-labels">
            <span class="usage-bar-used">
              <strong>{{ formatNumber(creditUsage) }}</strong>
              / {{ formatNumber(creditTotal) }} {{ 'Billing.Credits' | translate }}
            </span>
            <span class="usage-bar-pct">{{ creditUsagePercent }}% {{ 'Billing.Used' | translate }}</span>
          </div>
          <div class="usage-bar-track">
            <div class="usage-bar-fill" [style.width.%]="creditUsagePercent"></div>
          </div>
        </div>

        @if (periodStart && periodEnd) {
          <p class="period-hint mt-2">{{ 'Billing.CurrentPeriod' | translate: { start: periodStart | date: 'MMM d', end: periodEnd | date: 'MMM d' } }}</p>
        }
      </div>

      <!-- Card 2: Usage Breakdown Donut (feature-flagged) / Credit Overage -->
      @if (showUsageBreakdown) {
        <div class="bento-card bento-card--breakdown p-4">
          <h2 class="card-label mb-3">{{ 'Billing.UsageBreakdown' | translate }}</h2>
          <div class="donut-center">
            <div echarts [options]="donutOptions" class="donut-chart" style="width: 160px; height: 160px; position: relative; z-index: 1"></div>
            <div class="donut-label">
              <span class="donut-value">{{ formatNumber(animatedUsed) }}</span>
              <span class="donut-sub">{{ 'Billing.CreditsUsed' | translate }}</span>
            </div>
          </div>

          <!-- Legend -->
          <div class="breakdown-legend mt-3">
            @for (seg of visibleBreakdown; track seg.key) {
              <div class="legend-row">
                <div class="legend-left">
                  <div class="legend-dot" [style.background-color]="seg.color"></div>
                  <span class="legend-label">{{ seg.label | translate }}</span>
                </div>
                <span class="legend-value">{{ formatNumber(seg.credits) }}</span>
              </div>
            }
            @if (hasHiddenLegendItems) {
              <button class="legend-toggle" (click)="legendExpanded = !legendExpanded">
                <mat-icon class="legend-toggle-icon">{{ legendExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                {{ (legendExpanded ? 'Global.ShowLess' : 'Global.ShowMore') | translate }}
              </button>
            }
          </div>
        </div>
      } @else {
        <!-- Credit Overage Card -->
        <div class="bento-card bento-card--breakdown p-4">
          <div class="card-header justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-2">
              <h2 class="card-label mb-0">{{ 'Billing.CreditOverage' | translate }}</h2>
              <div class="card-icon-box" [class]="hasOverage ? 'card-icon-box--warning' : overageEnabled ? 'card-icon-box--success' : 'card-icon-box--neutral'">
                <mat-icon>{{ hasOverage ? 'warning' : overageEnabled ? 'check_circle' : 'info' }}</mat-icon>
              </div>
            </div>
            <div class="d-flex align-items-center">
              @if (isTogglingOverage) {
                <mat-spinner diameter="24"></mat-spinner>
              } @else if (hasPaymentMethod && overageEnabled) {
                <mat-slide-toggle [checked]="overageEnabled" (change)="onToggleOverage($event)" color="primary" [matTooltip]="'Billing.ToggleOverageTooltip' | translate"></mat-slide-toggle>
              }
            </div>
          </div>

          <div class="overage-display">
            <!-- Step 1: No payment method -->
            @if (!hasPaymentMethod) {
              <div class="overage-unlock-hint">
                <span class="material-symbols-rounded overage-unlock-icon">credit_card</span>
                <p class="hint-title">{{ 'Billing.AddPaymentMethodForOverage' | translate }}</p>
                <p class="hint-desc">{{ 'Billing.OverageHintDesc' | translate }}</p>
                <button class="overage-unlock-btn" (click)="addPaymentMethod()">
                  <span class="material-symbols-rounded">add</span>
                  {{ 'Billing.Action.AddPaymentMethod' | translate }}
                </button>
              </div>
            }
            <!-- Step 2: Has payment method but overage not activated -->
            @else if (!overageEnabled) {
              <div class="overage-unlock-hint">
                <span class="material-symbols-rounded overage-unlock-icon">trending_up</span>
                <p class="hint-title">{{ 'Billing.OverageActivateTitle' | translate }}</p>
                <p class="hint-desc">{{ 'Billing.OverageActivateDesc' | translate }}</p>
                <button class="overage-unlock-btn" btnType="payment" (click)="activateOverage()">
                  <span class="material-symbols-rounded">bolt</span>
                  {{ 'Billing.OverageActivateBtn' | translate }}
                </button>
              </div>
            }
            <!-- Step 3: Overage active with usage -->
            @else if (hasOverage) {
              <span class="overage-value overage-value--exceeded">{{ formatNumber(creditOverage) }}</span>
              <p class="overage-sub">{{ 'Billing.OverageCreditsUsed' | translate }}</p>
              <div class="overage-detail mt-3">
                <div class="overage-detail-row overage-detail-row--highlight">
                  <span class="overage-detail-label">{{ 'Billing.OverageApproxCost' | translate }}</span>
                  <span class="overage-detail-value overage-detail-value--warning">{{ creditOverage * 0.01 | number: '1.2-2' }} $ CAD</span>
                </div>
              </div>
              <p class="overage-note mt-3">{{ 'Billing.OverageNote' | translate }}</p>
              <p class="overage-equiv mt-2">{{ 'Billing.CreditEquiv' | translate }}</p>
            } @else {
              <span class="overage-value overage-value--ok">0</span>
              <p class="overage-sub">{{ 'Billing.NoOverage' | translate }}</p>
              <div class="overage-detail mt-3">
                <div class="overage-detail-row">
                  <span class="overage-detail-label">{{ 'Billing.OverageApproxCost' | translate }}</span>
                  <span class="overage-detail-value">{{ creditOverage * 0.01 | number: '1.2-2' }} $ CAD</span>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Card 3: Plan Card (Dark) -->
      <div class="bento-card bento-card--plan">
        <div>
          <div class="plan-header">
            <span class="material-symbols-rounded plan-crown">workspace_premium</span>
            <span class="plan-header-label">{{ 'Billing.YourPlan' | translate }}</span>
          </div>

          @if (subscriptionStatus === 'active') {
            <h3 class="plan-name">{{ 'Billing.Plan.Pro' | translate }}</h3>
            <p class="plan-price">{{ planPriceLabel }}</p>
            <p class="plan-detail">{{ 'Billing.Plan.ProCredits' | translate }}</p>
          } @else if (subscriptionStatus === 'trialing') {
            <h3 class="plan-name">{{ 'Billing.Plan.ProTrial' | translate }}</h3>
            <p class="plan-trial-remaining">{{ trialDaysRemaining }} {{ 'Billing.Plan.DaysRemaining' | translate }}</p>
            <p class="plan-detail">{{ 'Billing.Plan.TrialDetail' | translate }}</p>
          } @else if (subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') {
            <h3 class="plan-name">{{ 'Billing.Plan.Canceled' | translate }}</h3>
            <p class="plan-detail">{{ 'Billing.Plan.ReactivateDetail' | translate }}</p>
          } @else {
            <h3 class="plan-name">{{ 'Billing.Plan.Free' | translate }}</h3>
            <p class="plan-price">{{ 'Billing.Plan.LimitedFeatures' | translate }}</p>
            <p class="plan-detail">{{ 'Billing.Plan.UpgradeToUnlock' | translate }}</p>
          }
        </div>

        <div class="plan-actions">
          @if (subscriptionStatus === 'active') {
            <button class="plan-btn plan-btn--ghost" (click)="manageSubscription()">
              {{ 'Billing.Action.ManagePlan' | translate }}
              <span class="material-symbols-rounded plan-btn-icon">open_in_new</span>
            </button>
          } @else if (subscriptionStatus === 'trialing') {
            @if (!hasPaymentMethod) {
              <button class="plan-btn" btnType="payment" (click)="addPaymentMethod()" [disabled]="isUpgrading()">
                <span class="material-symbols-rounded plan-btn-icon">add_card</span>
                {{ 'Billing.Action.AddPaymentMethod' | translate }}
              </button>
            } @else {
              <button class="plan-btn" btnType="payment" (click)="convertTrial()" [disabled]="isConverting()">
                @if (isConverting()) {
                  <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
                } @else {
                  <span class="material-symbols-rounded plan-btn-icon">north_east</span>
                }
                {{ 'Billing.UpgradeNow' | translate }}
              </button>
              <button class="plan-btn plan-btn--link" (click)="manageSubscription()" [disabled]="isConverting()">{{ 'Billing.Action.ManageBilling' | translate }}</button>
            }
          } @else if (subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') {
            <button class="plan-btn" btnType="payment" (click)="manageSubscription()">
              <span class="material-symbols-rounded plan-btn-icon">north_east</span>
              {{ 'Billing.Action.Reactivate' | translate }}
            </button>
          } @else {
            <button class="plan-btn" btnType="payment" (click)="startTrial()" [disabled]="isUpgrading()">
              @if (isUpgrading()) {
                <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
              } @else {
                <span class="material-symbols-rounded plan-btn-icon">workspace_premium</span>
              }
              {{ 'Billing.Action.StartTrial' | translate }}
            </button>
          }
        </div>
      </div>
    </div>
  }

  <!-- Usage Table -->
  <mat-card class="elevated-table p-0 m-0">
    <!-- Filter Toolbar -->
    <div class="filter-toolbar">
      <div class="filter-fields">
        <div class="filter-field">
          <mat-label class="form-label">{{ 'Billing.Filter.DateRange' | translate }}</mat-label>
          <mat-form-field appearance="fill" subscriptSizing="dynamic">
            <mat-date-range-input [rangePicker]="rangePicker" [formGroup]="dateRangeGroup">
              <input matStartDate formControlName="start" [placeholder]="'Billing.Filter.From' | translate" />
              <input matEndDate formControlName="end" [placeholder]="'Billing.Filter.To' | translate" (dateChange)="onFilterChange()" />
            </mat-date-range-input>
            <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
            <mat-date-range-picker #rangePicker></mat-date-range-picker>
          </mat-form-field>
        </div>

        <div class="filter-field">
          <mat-label class="form-label">{{ 'Billing.Filter.Type' | translate }}</mat-label>
          <ui-select [compact]="true" [options]="typeOptions" [(ngModel)]="selectedType" (valueChange)="onFilterChange()" [placeholder]="'Billing.Filter.AllTypes' | translate"></ui-select>
        </div>
        <div class="filter-field">
          <mat-label class="form-label">{{ 'Billing.Filter.User' | translate }}</mat-label>
          <ui-select [compact]="true" [options]="userOptions" [(ngModel)]="selectedUserId" (valueChange)="onFilterChange()" [placeholder]="'Billing.Filter.AllUsers' | translate"></ui-select>
        </div>
      </div>
      @if (hasActiveFilters) {
        <div class="filter-actions">
          <button mat-button (click)="clearFilters()">
            <mat-icon>close</mat-icon>
            {{ 'Billing.Filter.Clear' | translate }}
          </button>
        </div>
      }
    </div>

    @if (showTiles()) {
      <!-- Mobile Tile View -->
      <div class="usage-tile-list custom-scrollbar" (scroll)="onTableScroll($event)">
        @for (element of dataSource.data; track element._id) {
          <div class="usage-tile">
            <div class="usage-tile__top">
              <span class="type-badge" [class]="getTypeColor(element.type)">
                {{ getTypeName(element.type) | translate }}
              </span>
              <span class="usage-tile__credits" [class.usage-tile__credits--zero]="element.cost === 0">{{ element.cost | number: '1.0-2' }} {{ 'Billing.Credits' | translate }}</span>
            </div>
            <div class="usage-tile__middle">
              <span class="usage-tile__quantity">
                <strong class="mono-val">{{ element.tokens | number: '1.0-2' }}</strong>
                <span class="text-muted">{{ getUnitName(element.unit) | translate }}</span>
              </span>
              <span class="usage-tile__date text-muted">{{ element.date | date: 'MMM d, y · h:mm a' }}</span>
            </div>
            <div class="usage-tile__bottom">
              <div class="user-cell">
                <div class="user-avatar">{{ element.user?.fullName?.charAt(0) | uppercase }}</div>
                <span class="user-email">{{ element.user?.fullName }}</span>
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- Desktop Table View -->
      <div class="table-scroll-container custom-scrollbar" (scroll)="onTableScroll($event)">
        <table mat-table [dataSource]="dataSource" matSort (matSortChange)="onSortChange($event)">
          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.Type' | translate }}</th>
            <td mat-cell *matCellDef="let element">
              {{ getTypeName(element.type) | translate }}
            </td>
          </ng-container>

          <ng-container matColumnDef="tokens">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.TokenCount' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="mono-val">{{ element.tokens | number: '1.0-2' }}</td>
          </ng-container>

          <ng-container matColumnDef="unit">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.Unit' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="text-muted">{{ getUnitName(element.unit) | translate }}</td>
          </ng-container>

          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.Date' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="text-muted">{{ element.date | date: 'MMM d, y, h:mm a' }}</td>
          </ng-container>

          <ng-container matColumnDef="cost">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.Cost' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cost-val">{{ element.cost | number: '1.0-2' }}</td>
          </ng-container>

          <ng-container matColumnDef="user">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Billing.User' | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <div class="user-cell">
                <div class="user-avatar">
                  {{ element.user?.fullName?.charAt(0) | uppercase }}
                </div>
                <span class="user-email" [matTooltip]="element.user?.fullName + ' ' + element.user?.email">{{ element.user?.fullName }}</span>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>
    }

    @if (loadingMore) {
      <div class="loading-more">
        <mat-spinner diameter="24"></mat-spinner>
        <span>{{ 'Global.LoadingMore' | translate }}</span>
      </div>
    }
  </mat-card>
</div>

<!-- Explanation Dialog Template -->
<ng-template #explanationDialog>
  <div class="standard-dialog">
    <div class="header" mat-dialog-title>
      <div class="title-section">
        <h2>{{ 'Billing.ExplanationTitle' | translate }}</h2>
        <p class="subtitle">{{ 'Billing.ExplanationSubtitle' | translate }}</p>
      </div>
      <button mat-icon-button mat-dialog-close class="close-btn">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <dl class="explanation-list">
        @for (item of costInfoDataSource.data; track item.name) {
          <div class="explanation-item">
            <dt>{{ item.name }}</dt>
            <dd>{{ item.description }}</dd>
          </div>
        }
      </dl>
    </mat-dialog-content>
  </div>
</ng-template>
